import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'

// Pages
import Login from './pages/Login'
import Register from './pages/Register'
import StudentDashboard from './pages/student/Dashboard'
import StudentChallenges from './pages/student/Challenges'
import StudentAchievements from './pages/student/Achievements'
import StudentSkills from './pages/student/Skills'
import StudentScanHistory from './pages/student/ScanHistory'
import StudentGameArcade from './pages/student/GameArcade'
import FactStrike from './pages/student/FactStrike'
import TestPrep from './pages/student/TestPrep'
import ParentDashboard from './pages/parent/Dashboard'
import ParentCoursesHub from './pages/parent/CoursesHub'
import ParentGamesHub from './pages/parent/GamesHub'
import ParentStudentDetail from './pages/parent/StudentDetail'
import ParentStudentProgress from './pages/parent/Progress'
import ParentChildScans from './pages/parent/ChildScans'
import ParentCourseDetail from './pages/parent/CourseDetail'
import ParentMessages from './pages/parent/Messages'
import ParentHomeworkReview from './pages/parent/HomeworkReview'
import ParentTestPrepGuide from './pages/parent/TestPrepGuide'
import ParentScans from './pages/parent/Scans'
import ParentSettings from './pages/parent/Settings'
import ParentSpellingTracker from './pages/parent/SpellingTracker'

// Components
import Layout from './components/Layout'
import LoadingScreen from './components/LoadingScreen'
import { ErrorBoundary } from './components/ErrorBoundary'

function App() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <ErrorBoundary>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        user ? <Navigate to={user.user_type === 'parent' ? '/parent' : '/student'} /> : <Login />
      } />
      <Route path="/register" element={
        user ? <Navigate to={user.user_type === 'parent' ? '/parent' : '/student'} /> : <Register />
      } />

      {/* Student routes */}
      <Route path="/student" element={
        user?.user_type === 'student' ? <Layout /> : <Navigate to="/login" />
      }>
        <Route index element={<StudentDashboard />} />
        <Route path="challenges" element={<StudentChallenges />} />
        <Route path="achievements" element={<StudentAchievements />} />
        <Route path="skills" element={<StudentSkills />} />
        <Route path="scans" element={<StudentScanHistory />} />
        <Route path="match" element={<Navigate to="/student/scans?tab=match" replace />} />
        <Route path="arcade" element={<StudentGameArcade />} />
        <Route path="test-prep" element={<TestPrep />} />
      </Route>

      {/* Full-screen game routes (no Layout wrapper) */}
      <Route path="/student/fact-strike" element={
        user?.user_type === 'student' ? <FactStrike /> : <Navigate to="/login" />
      } />

      {/* Parent routes */}
      <Route path="/parent" element={
        user?.user_type === 'parent' ? <Layout /> : <Navigate to="/login" />
      }>
        <Route index element={<ParentDashboard />} />

        {/* Scans hub */}
        <Route path="scans" element={<ParentScans />} />
        <Route path="scans/:sessionId" element={<ParentHomeworkReview />} />

        {/* Courses hub + student drill-down */}
        <Route path="courses" element={<ParentCoursesHub />} />
        <Route path="courses/student/:studentId" element={<ParentStudentDetail />} />
        <Route path="courses/student/:studentId/scans" element={<ParentChildScans />} />
        <Route path="courses/student/:studentId/progress" element={<ParentStudentProgress />} />
        <Route path="courses/student/:studentId/course/:courseId" element={<ParentCourseDetail />} />
        <Route path="courses/student/:studentId/test-prep" element={<ParentTestPrepGuide />} />

        {/* Games hub */}
        <Route path="games" element={<ParentGamesHub />} />
        <Route path="games/arcade" element={<StudentGameArcade />} />
        <Route path="spelling-tracker" element={<ParentSpellingTracker />} />

        {/* Other */}
        <Route path="messages" element={<ParentMessages />} />
        <Route path="settings" element={<ParentSettings />} />

        {/* Redirects from old routes */}
        <Route path="grades" element={<Navigate to="/parent/courses" replace />} />
        <Route path="homework" element={<Navigate to="/parent/scans" replace />} />
        <Route path="arcade" element={<Navigate to="/parent/games" replace />} />
        <Route path="game-stats" element={<Navigate to="/parent/games?tab=stats" replace />} />
        <Route path="children" element={<Navigate to="/parent/settings?section=children" replace />} />
        <Route path="spelling-lists" element={<Navigate to="/parent/games?tab=spelling" replace />} />
        <Route path="goals" element={<Navigate to="/parent/courses" replace />} />
        <Route path="tonight" element={<Navigate to="/parent/scans" replace />} />

        {/* Redirects from old student paths (without /courses/ prefix) */}
        <Route path="student/:studentId" element={<ParentStudentDetail />} />
        <Route path="student/:studentId/scans" element={<ParentChildScans />} />
        <Route path="student/:studentId/progress" element={<ParentStudentProgress />} />
        <Route path="student/:studentId/course/:courseId" element={<ParentCourseDetail />} />
        <Route path="students/:studentId/test-prep" element={<ParentTestPrepGuide />} />
      </Route>

      {/* Redirect root to login or dashboard */}
      <Route path="/" element={
        <Navigate to={user ? (user.user_type === 'parent' ? '/parent' : '/student') : '/login'} />
      } />

      {/* 404 */}
      <Route path="*" element={
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-2">404</h1>
            <p className="text-gray-400">Page not found</p>
          </div>
        </div>
      } />
    </Routes>
    </ErrorBoundary>
  )
}

export default App
